import React, { useEffect, useRef, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useApp } from "../context/AppContext";
import { getPaymentStatus } from "../lib/api";

const copy = {
  nl: {
    checking: "Betaling controleren…",
    success: "Bedankt voor je bestelling!",
    successSub: "Je betaling is gelukt. Je ontvangt een bevestiging per e-mail.",
    pending: "Je betaling wordt verwerkt…",
    failed: "Betaling niet voltooid",
    failedSub: "Er is iets misgegaan of de betaling is verlopen. Probeer het opnieuw.",
    home: "Terug naar home",
    shop: "Verder winkelen",
    order: "Bestelnummer",
  },
  en: {
    checking: "Checking payment…",
    success: "Thank you for your order!",
    successSub: "Your payment was successful. You'll receive a confirmation by email.",
    pending: "Your payment is being processed…",
    failed: "Payment not completed",
    failedSub: "Something went wrong or the payment expired. Please try again.",
    home: "Back to home",
    shop: "Continue shopping",
    order: "Order reference",
  },
};

const MAX_POLLS = 8;

const PaymentSuccess = () => {
  const { lang, clearCart } = useApp();
  const c = copy[lang];
  const location = useLocation();
  const [state, setState] = useState("checking"); // checking | paid | pending | failed
  const cleared = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("failed");
      return;
    }
    let polls = 0;
    let timer;
    const poll = async () => {
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) {
            clearCart();
            cleared.current = true;
          }
          return;
        }
        if (res.status === "expired" || res.status === "failed") {
          setState("failed");
          return;
        }
        polls += 1;
        if (polls >= MAX_POLLS) {
          setState("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        polls += 1;
        if (polls >= MAX_POLLS) {
          setState("pending");
          return;
        }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [location.search, clearCart]);

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-5 py-24">
      <div className="max-w-md w-full text-center bg-white rounded-3xl border border-[#EEDDD3] p-10 shadow-[0_30px_70px_-30px_rgba(160,100,80,0.35)]">
        {state === "checking" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-[#B4715C] animate-spin" />
            <h1 className="mt-6 font-serif text-3xl text-[#3A2C28]">{c.checking}</h1>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="h-14 w-14 mx-auto text-[#5FA37A]" />
            <h1 className="mt-6 font-serif text-4xl text-[#3A2C28]">{c.success}</h1>
            <p className="mt-4 text-[15px] text-[#6E5A52] leading-relaxed">{c.successSub}</p>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="h-12 w-12 mx-auto text-[#B4715C] animate-spin" />
            <h1 className="mt-6 font-serif text-3xl text-[#3A2C28]">{c.pending}</h1>
          </>
        )}
        {state === "failed" && (
          <>
            <XCircle className="h-14 w-14 mx-auto text-[#C8604B]" />
            <h1 className="mt-6 font-serif text-3xl text-[#3A2C28]">{c.failed}</h1>
            <p className="mt-4 text-[15px] text-[#6E5A52] leading-relaxed">{c.failedSub}</p>
          </>
        )}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link to="/" className="h-11 px-6 rounded-full bg-[#3A2C28] text-white text-[12px] tracking-label uppercase hover:bg-[#B4715C] flex items-center">
            {c.home}
          </Link>
          <Link to="/#collection" className="h-11 px-6 rounded-full border border-[#D6B7A9] text-[#5A4740] text-[12px] tracking-label uppercase hover:border-[#B4715C] hover:text-[#B4715C] flex items-center">
            {c.shop}
          </Link>
        </div>
      </div>
    </main>
  );
};

export default PaymentSuccess;
